import { supabase } from '../supabaseClient';
import { Trip } from '../types';
import { normalizeSlug, isValidSlug } from './profile';

// Suggest a trip slug from the title. E.g. "Spain (May 4–17, 2026)" → "spain-may-4-17-2026"
export function suggestTripSlug(title: string): string {
  return normalizeSlug(title).slice(0, 80) || 'trip';
}

export async function fetchTrip(tripId: string): Promise<{ data: Trip | null; error: string | null }> {
  const { data, error } = await supabase
    .from('trips')
    .select('*')
    .eq('trip_id', tripId)
    .maybeSingle();
  if (error) return { data: null, error: error.message };
  return { data: data as Trip | null, error: null };
}

export async function updateTrip(
  tripId: string,
  patch: Partial<Pick<Trip, 'title' | 'description' | 'slug' | 'cover_photo_path' | 'is_published' | 'is_public'>>,
): Promise<{ data: Trip | null; error: string | null }> {
  // Auto-manage published_at whenever is_published flips true.
  const updates: Record<string, unknown> = { ...patch, updated_at: new Date().toISOString() };
  if (patch.is_published === true) {
    updates.published_at = new Date().toISOString();
  }
  const { data, error } = await supabase
    .from('trips')
    .update(updates)
    .eq('trip_id', tripId)
    .select()
    .single();
  if (error) return { data: null, error: error.message };
  return { data: data as Trip, error: null };
}

export async function isTripSlugAvailable(
  slug: string,
  currentTripId: string,
): Promise<{ available: boolean; error: string | null }> {
  const { data: session } = await supabase.auth.getSession();
  const uid = session.session?.user.id;
  if (!uid) return { available: false, error: 'Not signed in' };
  // Slugs are unique per user (partial unique index), so scope by user_id.
  const { data, error } = await supabase
    .from('trips')
    .select('trip_id')
    .eq('user_id', uid)
    .eq('slug', slug)
    .neq('trip_id', currentTripId)
    .maybeSingle();
  if (error) return { available: false, error: error.message };
  return { available: !data, error: null };
}

// Validate a trip slug (looser than profile slug — allows up to 80 chars).
export const TRIP_SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{1,79}$/;
export function isValidTripSlug(slug: string): boolean {
  return TRIP_SLUG_PATTERN.test(slug);
}

export { isValidSlug };
